import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAntTheme } from "hooks/useAntTheme";
import { useOne, useTranslate } from "@refinedev/core";
import { Typography, MenuProps } from "antd";
import dayjs from "dayjs";
import styled from "styled-components";
import { DownloadSimpleIcon, ShareNetworkIcon } from "@phosphor-icons/react";
import { LoadingOutlined } from "@ant-design/icons";
import {
  MediaDocument,
  MediaProjectListItem,
  MediaProjectResponse,
} from "../../types";
import { addDateTz } from "../../utils";
import {
  getProjectDetailRoute,
  getProjectStatusDotColor,
  mapProjectStatus,
} from "../../projects/helpers/helper";
import { MenuCTA } from "components/MenuCTA";
import { Button } from "antd";
import axiosInstance from "services/api";
import { radius } from "@libertify/ui";

export type RecentProjectCardProps = {
  project: MediaProjectListItem;
  onDeleteSuccess?: () => void | Promise<unknown>;
};

const THUMBNAIL_GRADIENTS = [
  "linear-gradient(135deg, #E0F7F5 0%, #B2E8E2 100%)", // teal
  "linear-gradient(135deg, #FFE8E8 0%, #FFC4C4 100%)", // soft red
  "linear-gradient(135deg, #FFF4D6 0%, #FFE08A 100%)", // warm yellow
  "linear-gradient(135deg, #E6E9FF 0%, #B8C0FF 100%)", // periwinkle
  "linear-gradient(135deg, #F3E5FF 0%, #D7B8FF 100%)", // lavender
  "linear-gradient(135deg, #E0F2FE 0%, #A6D8F7 100%)", // sky blue
  "linear-gradient(135deg, #FFE5D9 0%, #FFB59A 100%)", // peach
  "linear-gradient(135deg, #E5F5E0 0%, #A8DDA0 100%)", // mint green
  "linear-gradient(135deg, #FCE4F0 0%, #F4B6D5 100%)", // pink
];

const getGradientForId = (id: string | number): string => {
  const key = String(id);
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  const index = hash % THUMBNAIL_GRADIENTS.length;
  return THUMBNAIL_GRADIENTS[index] as string;
};

export const RecentProjectCard = ({
  project,
  onDeleteSuccess,
}: RecentProjectCardProps) => {
  const navigate = useNavigate();
  const { theme } = useAntTheme();
  const t = useTranslate();

  // The list endpoint returns MediaProjectHeader which does not include
  // storyboards. Fetch the full project details to read the first objective.
  const { data: projectDetailsData } = useOne<MediaProjectResponse>({
    resource: "media/projects",
    id: project.id,
    queryOptions: {
      enabled: Boolean(project.id),
    },
  });

  const firstObjective =
    projectDetailsData?.data.storyboards?.[0]?.objectives?.[0];

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadDocument = async () => {
    if (!project.document_id || !project.organization_id) return;
    setIsDownloading(true);
    try {
      const { data } = await axiosInstance.get<MediaDocument>(
        `media/documents/${project.organization_id}/${project.document_id}`
      );
      if (data.url) {
        globalThis.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Failed to download document", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadDocumentItem: MenuProps["items"] = project.document_id
    ? [
        {
          key: "download-document",
          label: (
            <Button
              size="small"
              type="text"
              icon={
                isDownloading ? (
                  <LoadingOutlined spin />
                ) : (
                  <DownloadSimpleIcon />
                )
              }
              disabled={isDownloading}
              style={{ background: "transparent" }}
            >
              {project.document_name || t("actions.downloadDocument")}
            </Button>
          ),
          onClick: (e) => {
            e.domEvent.stopPropagation();
            handleDownloadDocument().catch((error) => {
              console.error("Failed to download document", error);
            });
          },
        },
      ]
    : [];

  return (
    <RecentProjectCardWrapper
      role="link"
      tabIndex={0}
      aria-label={project.title}
      onClick={() =>
        navigate(getProjectDetailRoute(project.id, project.type))
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(getProjectDetailRoute(project.id, project.type));
        }
      }}
    >
      <RecentProjectCardThumbnail $gradient={getGradientForId(project.id)}>
        {firstObjective && (
          <RecentProjectCardTag>{firstObjective}</RecentProjectCardTag>
        )}
      </RecentProjectCardThumbnail>

      <RecentProjectCardInfo>
        <RecentProjectCardInfoText>
          <Typography.Text
            ellipsis
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 16,
              fontWeight: 600,
              lineHeight: "19px",
              color: theme.colorTextBase,
            }}
          >
            {project.title}
          </Typography.Text>

          <RecentProjectCardStatus>
            <StatusDot $color={getProjectStatusDotColor(project.status)} />
            <StatusLabel>
              {t(
                `media.components.projectQuickAccess.${mapProjectStatus(project.status)}`
              )}
            </StatusLabel>
            <RecentProjectCardDate>
              {dayjs(addDateTz(project.created ?? undefined)).fromNow()}
            </RecentProjectCardDate>
          </RecentProjectCardStatus>
        </RecentProjectCardInfoText>

        <RecentProjectCardActions>
          {/* TODO: Share button disabled until functionality is implemented */}
          {/* <RecentProjectCardActionButton
            onClick={(e) => e.stopPropagation()}
            aria-label={`Share ${project.title}`}
          >
            <ShareNetworkIcon size={16} />
          </RecentProjectCardActionButton> */}
          <MenuCTAWrapper onClick={(e) => e.stopPropagation()}>
            <MenuCTA
              recordId={project.id}
              onEditClick={() =>
                navigate(getProjectDetailRoute(project.id, project.type))
              }
              onDeleteSuccess={onDeleteSuccess}
              extraItems={downloadDocumentItem}
            />
          </MenuCTAWrapper>
        </RecentProjectCardActions>
      </RecentProjectCardInfo>
    </RecentProjectCardWrapper>
  );
};

const RecentProjectCardWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 274px;
  border-radius: ${radius.box}px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colorBgElevated};
  cursor: pointer;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colorPrimary};
    outline-offset: 2px;
  }

  @media (width <= 768px) {
    width: 100%;
    max-width: 345px;
  }
`;

const RecentProjectCardThumbnail = styled.div<{ $gradient: string }>`
  position: relative;
  width: 100%;
  height: 176px;
  background: ${({ $gradient }) => $gradient};
`;

const RecentProjectCardTag = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 3px 10px;
  border-radius: 2px;
  background-color: #ecfffe;
  color: #247f79;
  font-family: "Inter", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 15px;
`;

const RecentProjectCardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 24px;
`;

const RecentProjectCardInfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const RecentProjectCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const RecentProjectCardActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${radius.circle};
  border: none;
  background: none;
  color: ${({ theme }) => theme.colorTextSecondary};
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${({ theme }) => theme.colorBgLayout};
  }
`;

const MenuCTAWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const RecentProjectCardStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: ${radius.circle};
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const StatusLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colorTextBase};
`;

const RecentProjectCardDate = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${({ theme }) => theme.colorTextSecondary};
`;
